import { LOCALIZED_FALLBACK_ORDER } from '../config/languages';
import en from './locales/en';
import { BOOKING_PERSON_PAGE_OVERLAY } from './bookingPersonPageOverlay';

/**
 * Per-language string trees. English is in the main bundle; other locales load
 * on demand via ensureLocale() so Lighthouse main.js stays smaller.
 *
 * If a key is missing in the active language, t() falls back through
 * LOCALIZED_FALLBACK_ORDER (defined in src/config/languages.js).
 */
export const LOCALES = {
  en,
};

const localeLoaders = {
  hi: () => import(/* webpackChunkName: "i18n-hi" */ './locales/hi'),
  te: () => import(/* webpackChunkName: "i18n-te" */ './locales/te'),
  kn: () => import(/* webpackChunkName: "i18n-kn" */ './locales/kn'),
  ta: () => import(/* webpackChunkName: "i18n-ta" */ './locales/ta'),
  ml: () => import(/* webpackChunkName: "i18n-ml" */ './locales/ml'),
  mr: () => import(/* webpackChunkName: "i18n-mr" */ './locales/mr'),
  gu: () => import(/* webpackChunkName: "i18n-gu" */ './locales/gu'),
  bn: () => import(/* webpackChunkName: "i18n-bn" */ './locales/bn'),
  pa: () => import(/* webpackChunkName: "i18n-pa" */ './locales/pa'),
  or: () => import(/* webpackChunkName: "i18n-or" */ './locales/or'),
  as: () => import(/* webpackChunkName: "i18n-as" */ './locales/as'),
  ne: () => import(/* webpackChunkName: "i18n-ne" */ './locales/ne'),
};

const localePromises = {};

/** Load a non-EN locale chunk once; safe to call repeatedly. */
export function ensureLocale(code) {
  const lang = String(code || 'en').trim().toLowerCase();
  if (!lang || lang === 'en' || LOCALES[lang]) return Promise.resolve(LOCALES[lang] || LOCALES.en);
  const loader = localeLoaders[lang];
  if (!loader) return Promise.resolve(LOCALES.en);
  if (!localePromises[lang]) {
    localePromises[lang] = loader()
      .then((mod) => {
        LOCALES[lang] = mod.default || mod;
        return LOCALES[lang];
      })
      .catch(() => LOCALES.en);
  }
  return localePromises[lang];
}

/** Prefetch active UI language from URL / storage (call early from bootstrap). */
export function prefetchActiveLocale() {
  if (typeof window === 'undefined') return Promise.resolve(LOCALES.en);
  try {
    const params = new URLSearchParams(window.location.search || '');
    const fromUrl = (params.get('lang') || '').trim().toLowerCase();
    const fromStorage = (localStorage.getItem('daiv_lang') || '').trim().toLowerCase();
    const code = fromUrl || fromStorage || 'en';
    return ensureLocale(code);
  } catch {
    return Promise.resolve(LOCALES.en);
  }
}

function getByPath(obj, keys) {
  let cur = obj;
  for (let i = 0; i < keys.length; i++) {
    if (cur == null) return undefined;
    cur = cur[keys[i]];
  }
  return cur;
}

/** Get localized string for a key path, e.g. t('nav.home', lang) */
export function t(keyPath, lang = 'en') {
  if (!keyPath) return '';
  const keys = String(keyPath).split('.');
  const order = [lang, ...LOCALIZED_FALLBACK_ORDER.filter((c) => c !== lang)];
  for (const code of order) {
    const v = getByPath(LOCALES[code], keys);
    if (typeof v === 'string' && v) return v;
    if (keys[0] === 'booking' && keys.length >= 2) {
      const ov = getByPath(BOOKING_PERSON_PAGE_OVERLAY[code], [keys[1]]);
      if (typeof ov === 'string' && ov) return ov;
    }
  }
  return keyPath;
}

/** Replace `{name}` placeholders in a translated string. */
export function tFormat(keyPath, lang = 'en', vars = {}) {
  let s = t(keyPath, lang);
  for (const [k, v] of Object.entries(vars)) {
    s = s.split(`{${k}}`).join(String(v ?? ''));
  }
  return s;
}

/**
 * Backward-compatible TRANSLATIONS export – nested object whose leaves are
 * language maps. Values resolve from LOCALES at read time so lazy locales work.
 */
function buildTranslations() {
  function walk(node, path) {
    if (typeof node === 'string') {
      return new Proxy(
        {},
        {
          get(_target, lang) {
            if (typeof lang === 'symbol') return undefined;
            const v = getByPath(LOCALES[lang], path);
            if (typeof v === 'string') return v;
            const fallback = getByPath(LOCALES.en, path);
            return typeof fallback === 'string' ? fallback : undefined;
          },
          ownKeys() {
            return Object.keys(LOCALES).filter((c) => typeof getByPath(LOCALES[c], path) === 'string');
          },
          getOwnPropertyDescriptor(_t, lang) {
            if (typeof getByPath(LOCALES[lang], path) === 'string' || typeof getByPath(LOCALES.en, path) === 'string') {
              return { enumerable: true, configurable: true };
            }
            return undefined;
          },
        },
      );
    }
    if (node && typeof node === 'object') {
      const out = {};
      for (const k of Object.keys(node)) {
        out[k] = walk(node[k], [...path, k]);
      }
      return out;
    }
    return node;
  }
  return walk(en, []);
}

export const TRANSLATIONS = buildTranslations();
